import React, { useState, useEffect } from "react";
import { Button, Tag } from "antd";
import {
  FileExcelOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import GenericTable from "../../../Components/Tables/GenericTable";
import DateFilter from "../../../Components/DateFilter";
import * as XLSX from "xlsx";
import "./style.css";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import { getVendorSettlements } from "../../../API/FuelVendor/Settlement";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import { getInitials } from "../../../Components/InitialsAvatar";
import SettlementTransactionsModal from "../../../Components/Modals/SettlementTransactionsModal";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const formatLabel = (value) =>
  value
    ? value
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : value;

const Settlement = () => {
  const [statusFilter, setStatusFilter] = useState(null);
  const [settlementData, setSettlementData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [viewingSettlement, setViewingSettlement] = useState(null);
  const { from, to } = useDateFilter();

  useEffect(() => {
    const fetchSettlements = async () => {
      try {
        const startDate = from ? from.split(" ")[0] : undefined;
        const endDate = to ? to.split(" ")[0] : undefined;

        const res = await getVendorSettlements(startDate, endDate);

        if (Math.floor(res?.status / 100) === 2) {
          const data = res?.data?.data;

          setSettlementData(
            (data?.settlements || []).map((item, index) => ({
              ...item,
              key: item.settlement_id ?? item.id ?? index,
            })),
          );
          setSummary(data?.summary || null);
        }
      } catch (err) {
        console.error("Failed to load settlements:", err);
      }
    };

    fetchSettlements();
  }, [from, to]);

  const totalPaymentsMade = parseFloat(summary?.completed_total) || 0;
  const totalOutstanding = parseFloat(summary?.outstanding_total) || 0;

  const filteredTableData = statusFilter
    ? settlementData.filter((item) => item.status === statusFilter)
    : settlementData;

  const getUniqueFilters = (data = [], key) =>
    [...new Set(data.map((item) => item?.[key]).filter(Boolean))].map(
      (value) => ({
        text: formatLabel(value),
        value,
      }),
    );

  const paymentMethodFilters = getUniqueFilters(settlementData, "payment_method");
  const statusFilters = getUniqueFilters(settlementData, "status");

  const settlementColumns = [
    {
      title: "Date/Time",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (text) => {
        const [datePart, timePart] = (text || "").split(" ");
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              lineHeight: "1.2",
            }}
          >
            <span>{datePart}</span>
            <span style={{ color: "#71717A" }}>{timePart}</span>
          </div>
        );
      },
    },
    {
      title: "Settlement Id",
      dataIndex: "settlement_id",
      key: "settlement_id",
      enableSearch: true,
      render: (id) => <span style={{ color: "#4F46E5" }}>{id}</span>,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: true,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            className="vendor-avatar-circle"
            style={{ background: getAvatarColor(text) }}
          >
            {getInitials(text)}
          </span>
          {text}
        </span>
      ),
    },
    {
      title: "Gross Sales",
      dataIndex: "gross_sales",
      key: "gross_sales",
      sorter: (a, b) => (parseFloat(a.gross_sales) || 0) - (parseFloat(b.gross_sales) || 0),
      render: (val) => <span>{formatCurrency(val)}</span>,
    },
    {
      title: "Transactions",
      dataIndex: "transaction_count",
      key: "transaction_count",
      sorter: (a, b) => (a.transaction_count || 0) - (b.transaction_count || 0),
      render: (count, record) => {
        const total = count ?? record.transactions?.length ?? 0;
        const label = `${total} txn${total === 1 ? "" : "s"}`;

        if (!record.transactions?.length) {
          return (
            <span className="settlement-txn-chip settlement-txn-chip--empty">
              <UnorderedListOutlined />
              {label}
            </span>
          );
        }

        return (
          <button
            type="button"
            className="settlement-txn-chip"
            onClick={(e) => {
              e.stopPropagation();
              setViewingSettlement({
                title: record.settlement_id,
                transactions: record.transactions,
              });
            }}
          >
            <UnorderedListOutlined />
            {label}
            <EyeOutlined className="settlement-txn-chip__view" />
          </button>
        );
      },
    },
    {
      title: "Fees",
      dataIndex: "fees",
      key: "fees",
      sorter: (a, b) => (parseFloat(a.fees) || 0) - (parseFloat(b.fees) || 0),
      render: (val, record) => (
        <span style={{ color: "#71717A" }}>
          -{formatCurrency(val)}
          {record.fee_percent ? ` (${parseFloat(record.fee_percent)}%)` : ""}
        </span>
      ),
    },
    {
      title: "Net Payable",
      dataIndex: "net_payable",
      key: "net_payable",
      sorter: (a, b) => (parseFloat(a.net_payable) || 0) - (parseFloat(b.net_payable) || 0),
      render: (val) => <span>{formatCurrency(val)}</span>,
    },
    {
      title: "Running Total",
      dataIndex: "running_total",
      key: "running_total",
      sorter: (a, b) => (parseFloat(a.running_total) || 0) - (parseFloat(b.running_total) || 0),
      render: (value) => (
        <span style={{ fontWeight: 700 }}>{formatCurrency(value)}</span>
      ),
    },
    {
      title: "Paid By",
      dataIndex: "paid_by_name",
      key: "paid_by_name",
      enableSearch: true,
      render: (name) => name || "-",
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      filters: paymentMethodFilters,
      onFilter: (value, record) => record.payment_method === value,
      render: (method) => (
        <span className="fuel-type-badge">{formatLabel(method) || "-"}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span className={`status-badge ${(status || "").toLowerCase()}`}>
          {formatLabel(status)}
        </span>
      ),
    },
    {
      title: "Proof",
      key: "proof",
      render: (_, record) => {
        const fileUrl = resolveFileUrl(record.file_url);
        if (!fileUrl) return "-";
        return (
          <EyeOutlined
            style={{ cursor: "pointer", fontSize: 18, color: "#4F46E5" }}
            onClick={() => window.open(fileUrl, "_blank")}
          />
        );
      },
    },
  ];

  const handleExport = () => {
    const exportData = filteredTableData.map((item) => ({
      Date: item.date,
      "Settlement ID": item.settlement_id,
      Vendor: item.vendor,
      "Gross Sales": item.gross_sales,
      Fees: item.fees,
      "Net Payable": item.net_payable,
      "Running Total": item.running_total,
      "Paid By": item.paid_by_name,
      "Payment Method": formatLabel(item.payment_method),
      Status: formatLabel(item.status),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Settlement");
    XLSX.writeFile(workbook, "settlement-data.xlsx");
  };

  return (
    <div className="settlement">
      <div className="date-filter-top">
        <DateFilter />
      </div>
      <div className="settlement-row">
        <div
          onClick={() =>
            setStatusFilter(statusFilter === "completed" ? null : "completed")
          }
          style={{ cursor: "pointer", flex: 1, minWidth: "280px" }}
        >
          <GenericStatCard
            title="Completed Payments"
            value={formatCurrency(totalPaymentsMade)}
            icon={<CheckCircleOutlined style={{ color: "#16A34A" }} />}
            subtitle="Settled transaction amounts"
          />
        </div>

        <div
          onClick={() =>
            setStatusFilter(statusFilter === "pending" ? null : "pending")
          }
          style={{ cursor: "pointer", flex: 1, minWidth: "280px" }}
        >
          <GenericStatCard
            title="Outstanding Payments "
            value={formatCurrency(totalOutstanding)}
            icon={<ClockCircleOutlined style={{ color: "#F59E0B" }} />}
            subtitle="Pending settlement amounts"
          />
        </div>
      </div>

      {statusFilter && (
        <div style={{ marginBottom: "16px" }}>
          <Tag
            color={statusFilter === "completed" ? "success" : "warning"}
            closable
            closeIcon={<CloseCircleOutlined />}
            onClose={() => setStatusFilter(null)}
            style={{ padding: "6px 12px", fontSize: "14px", fontWeight: "400" }}
          >
            Showing {formatLabel(statusFilter)} Only
          </Tag>
        </div>
      )}

      <div className="settlement-header">
        <div className="export-wrapper">
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            className="export-pdf-btn"
            onClick={handleExport}
          >
            Export
          </Button>
        </div>
      </div>

      <GenericTable
        title="Settlement"
        dataSource={filteredTableData}
        columns={settlementColumns}
      />

      <SettlementTransactionsModal
        open={!!viewingSettlement}
        onClose={() => setViewingSettlement(null)}
        settlement={viewingSettlement}
      />
    </div>
  );
};

export default Settlement;
